'use client';

import { useEffect, useMemo, useState } from 'react';
import { DiscoveryPageLayout } from '@/domains/paydirt/layout';
import {
  listMaterials,
  getMaterialCommunityPrice,
  getMaterialSpotPrice,
  type CommunityMaterialPrice,
  type SpotMaterialPrice,
  type MaterialCatalogItem,
} from '@/domains/paydirt/materials/api';
import { MaterialCatalogCard } from '@/domains/paydirt/materials/MaterialCatalogCard';
import { MaterialCatalogToolbar } from '@/domains/paydirt/materials/MaterialCatalogToolbar';
import {
  type CatalogMaterial,
  GROUP_LABELS,
  groupMaterials,
  orderedGroupEntries,
} from '@/domains/paydirt/materials/materialCatalogGroups';
import {
  emptyFilterMessage,
  filterMaterials,
  filtersActive,
  type TypeFilter,
} from '@/domains/paydirt/materials/materialCatalogFilters';
import { materialPriceSummary } from '@/domains/paydirt/materials/materialPriceSummary';

export default function MaterialsIndexClient() {
  const [materials, setMaterials] = useState<MaterialCatalogItem[]>([]);
  const [communityPrices, setCommunityPrices] = useState<Record<string, CommunityMaterialPrice>>({});
  const [spotPrices, setSpotPrices] = useState<Record<string, SpotMaterialPrice>>({});
  const [search, setSearch] = useState('');
  const [type, setType] = useState<TypeFilter>('all');

  useEffect(() => {
    listMaterials()
      .then((d) => setMaterials(d.materials ?? []))
      .catch(() => setMaterials([]));
  }, []);

  useEffect(() => {
    const community = materials.filter((m) => m.valuationMethod === 'community');
    Promise.all(
      community.map((m) =>
        getMaterialCommunityPrice(m.materialId)
          .then((p) => [m.materialId, p] as const)
          .catch(() => null),
      ),
    ).then((rows) => {
      const map: Record<string, CommunityMaterialPrice> = {};
      for (const row of rows) {
        if (row) map[row[0]] = row[1];
      }
      setCommunityPrices(map);
    });

    const spots = materials.filter((m) => m.valuationMethod === 'spot');
    Promise.all(
      spots.map((m) =>
        getMaterialSpotPrice(m.materialId)
          .then((p) => [m.materialId, p] as const)
          .catch(() => null),
      ),
    ).then((rows) => {
      const map: Record<string, SpotMaterialPrice> = {};
      for (const row of rows) {
        if (row) map[row[0]] = row[1];
      }
      setSpotPrices(map);
    });
  }, [materials]);

  const filtered = useMemo(() => filterMaterials(materials, search, type), [materials, search, type]);
  const showSections = !filtersActive(search, type);

  function renderCard(m: CatalogMaterial) {
    return (
      <li key={m.materialId}>
        <MaterialCatalogCard
          material={m}
          priceSummary={materialPriceSummary(m, communityPrices, spotPrices)}
        />
      </li>
    );
  }

  return (
    <DiscoveryPageLayout
      title="Price guide"
      description="Community-driven reference prices for gems and minerals on Paydirt."
      toolbar={
        <MaterialCatalogToolbar
          search={search}
          type={type}
          totalCount={materials.length}
          filteredCount={filtered.length}
          onSearchChange={setSearch}
          onTypeChange={setType}
          onReset={() => {
            setSearch('');
            setType('all');
          }}
        />
      }
    >
      <p className="text-muted">
        Reference prices come from community submissions on each material page. Precious gem prices are per stone at a
        given carat size; semi-precious gems use $/gram; metals use FRED spot indexes.
      </p>

      {filtered.length === 0 ? (
        <p className="material-catalog-empty">{emptyFilterMessage(search, type)}</p>
      ) : showSections ? (
        orderedGroupEntries(groupMaterials(filtered)).map(([category, items]) => (
          <section key={category} className="material-catalog-section">
            <h2>{GROUP_LABELS[category] ?? category}</h2>
            <ul className="material-catalog-grid">{items.map(renderCard)}</ul>
          </section>
        ))
      ) : (
        <ul className="material-catalog-grid">{filtered.map(renderCard)}</ul>
      )}
    </DiscoveryPageLayout>
  );
}
